import { useEffect, useState } from "react"

const HIDDEN_BETS_KEY = "matches_placed"
const TTL_MS = 48 * 60 * 60 * 1000 // 48 hours

type HiddenMap = Record<string, number> // id -> expiry timestamp

export function useMatchesPlaced() {
  const [matchesPlaced, setMatchesPlaced] = useState<HiddenMap>({})
  const [hydrated, setHydrated] = useState(false)

  // Load from localStorage and prune expired
  useEffect(() => {
    if (typeof window === "undefined") return
    try {
      const stored = localStorage.getItem(HIDDEN_BETS_KEY)
      if (stored) {
        const parsed: HiddenMap = JSON.parse(stored)
        const now = Date.now()
        // prune expired
        const valid: HiddenMap = {}
        for (const [id, expiry] of Object.entries(parsed)) {
          if (expiry > now) {
            valid[id] = expiry
          }
        }
        setMatchesPlaced(valid)
      }
    } catch (err) {
      console.error("Failed to parse hidden bets", err)
    } finally {
      setHydrated(true)
    }
  }, [])

  // Sync to localStorage when hiddenBets changes
  useEffect(() => {
    if (!hydrated) return
    localStorage.setItem(HIDDEN_BETS_KEY, JSON.stringify(matchesPlaced))
  }, [matchesPlaced, hydrated])

  const matchPlaced = (id: string) => {
    const expiry = Date.now() + TTL_MS
    setMatchesPlaced(prev => ({ ...prev, [id]: expiry }))
  }

  const unhideMatch = (id: string) => {
    setMatchesPlaced(prev => {
      const updated = { ...prev }
      delete updated[id]
      return updated
    })
  }

  const matchIsPlaced = (id: string) => {
    const expiry = matchesPlaced[id]
    if (!expiry) return false
    if (expiry < Date.now()) {
      // expired → cleanup lazily
      unhideMatch(id)
      return false
    }
    return true
  }

  return { matchesPlaced, matchPlaced, unhideMatch, matchIsPlaced, hydrated }
}
